import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck, User, ShieldCheck, Package, Storefront } from '@phosphor-icons/react';
import api from '../lib/api';

const ROLES = [
  { key: 'admin', label: 'Admin', icon: ShieldCheck, color: 'bg-[#002FA7]' },
  { key: 'pilot', label: 'Pilot', icon: Truck, color: 'bg-[#00C853]' },
  { key: 'booking_staff', label: 'Booking', icon: Package, color: 'bg-[#FF8C00]' },
  { key: 'pickup', label: 'Pickup', icon: Storefront, color: 'bg-[#8E44AD]' },
];

export default function LoginPage() {
  const [selectedRole, setSelectedRole] = useState('admin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Phase 76t (Feb-2026) — Fetch latest build info (APK + server) and
  // display on the login screen so support can verify which build a user
  // is on at a glance ("Are you on 2.0.6?").
  const [versionInfo, setVersionInfo] = useState({ apk: null, server: null });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [rel, srv] = await Promise.all([
          api.get('/app/latest-release').catch(() => ({ data: null })),
          api.get('/app/version').catch(() => ({ data: null })),
        ]);
        setVersionInfo({
          apk: rel.data ? { name: rel.data.version_name, code: rel.data.version_code } : null,
          server: srv.data ? srv.data.version : null,
        });
      } catch { /* ignore */ }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password, selectedRole);
      if (data.role === 'admin') navigate('/admin');
      else if (data.role === 'pilot') navigate('/pilot');
      else if (data.role === 'pickup') navigate('/pickup');
      else navigate('/booking');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A0A0A] relative items-center justify-center overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/dd97d66a-ecb6-4a46-946b-d0bc461ee230/images/d9c58d96228e9fbe366a1aa754d149e8e5be16ebabc6543406252e01b966b675.png"
          alt="Fleet"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative z-10 p-12 text-center">
          <img src="/carloo-logo.png" alt="Carloo" className="h-20 mx-auto invert" />
          <p className="text-white/50 mt-4 text-base font-light tracking-[0.3em] uppercase">Vehicle Allocation & Monitoring</p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F7F7F7]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <img src="/carloo-logo.png" alt="Carloo" className="h-14 mx-auto" />
            <p className="text-[#666] text-xs mt-2 uppercase tracking-[0.2em]">Vehicle Allocation & Monitoring</p>
          </div>

          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E44AD] font-bold mb-1" data-testid="login-version-label">
            {versionInfo.apk ? `Version ${versionInfo.apk.name} (build ${versionInfo.apk.code})` : 'Version 2.0'}
            {versionInfo.server && versionInfo.apk?.name !== versionInfo.server && ` · server ${versionInfo.server}`}
          </p>
          <h2 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Sign In</h2>
          <p className="text-[#666] text-sm mt-1 mb-8">Select your role and enter credentials</p>

          {/* Role Selection */}
          <div className="flex gap-2 mb-8">
            {ROLES.map(r => (
              <button
                key={r.key}
                data-testid={`role-${r.key}`}
                onClick={() => { setSelectedRole(r.key); setEmail(''); setError(''); }}
                className={`flex-1 flex flex-col items-center gap-2 p-4 border transition-all duration-150 ${
                  selectedRole === r.key
                    ? 'border-[#002FA7] bg-[#002FA7]/5'
                    : 'border-[#E5E5E5] bg-white hover:border-[#002FA7]/30'
                }`}
              >
                <r.icon size={24} weight={selectedRole === r.key ? 'bold' : 'regular'} className={selectedRole === r.key ? 'text-[#002FA7]' : 'text-[#666]'} />
                <span className={`text-xs uppercase tracking-widest font-medium ${selectedRole === r.key ? 'text-[#002FA7]' : 'text-[#666]'}`}>{r.label}</span>
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-[#FF3333]/10 border border-[#FF3333]/30 text-[#FF3333] text-sm p-3" data-testid="login-error">
                {error}
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest font-medium text-[#666]">
                Mobile Number
              </Label>
              <Input
                data-testid="login-email"
                type="tel"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your mobile number"
                className="mt-1 border-[#E5E5E5] rounded-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-medium text-[#666]">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1 border-[#E5E5E5] rounded-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                required
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none h-12 text-sm uppercase tracking-widest font-medium"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
